/** Header-action builder for the Employers screen.
 *  Owns three mutually exclusive states: the directory (search, status filter,
 *  Add), a drilled-in employer profile (back chevron, Email/Call, and
 *  Cancel/Save once edited), and the add/edit form (back chevron + Cancel/Save). All actions fire window events the
 *  employers feature listens for. No JSX — the status filter's native <select>
 *  is drawn by an id-intercept in UnifiedAppHeader's renderLeadingAction, the
 *  same split `workspace-users-filter` uses. */

import { Search, SlidersHorizontal, Plus, Loader2, Mail, Phone } from '@/shared/ui/icons';
import {
  EMPLOYERS_CLEAR_SELECTION_EVENT,
  EMPLOYERS_CREATE_EVENT,
  EMPLOYER_DETAIL_CANCEL_EVENT,
  EMPLOYER_DETAIL_SAVE_EVENT,
  EMPLOYER_FORM_CANCEL_EVENT,
  EMPLOYER_FORM_SAVE_EVENT,
  EMPLOYER_STATUS_FILTER_ALL,
  EMPLOYER_STATUS_FILTER_OPTIONS,
  type EmployerStatusFilter,
} from '../../../features/employers/constants';
import type { MobileHeaderAction } from './types';

/** What EmployerForm broadcasts while it is mounted. */
export interface EmployersFormState {
  isEdit: boolean;
  isSubmitting: boolean;
  hasChanges: boolean;
}

/** What EmployerProfilePanel broadcasts while a profile is drilled in. There is
 *  no `editing` flag because the profile has no edit mode — `hasChanges` alone
 *  decides whether Cancel/Save exist. The addresses come over the wire because
 *  the header owns the buttons that act on them and has no other route to the
 *  employer record. */
export interface EmployersDetailState {
  isSubmitting: boolean;
  hasChanges: boolean;
  email: string | null;
  phone: string | null;
}

export interface EmployersHeaderCtx {
  /** The directory is on screen — no profile drilled in, no form open. The
   *  pills key off this rather than "the screen is mounted", so search /
   *  filter / Add can never point at a list nobody can see. */
  listVisible: boolean;
  /** Name of the drilled-in employer, or null. Present means the profile owns
   *  the content area, which is the only state that needs a bare back chevron. */
  selectedName: string | null;
  statusFilter: EmployerStatusFilter;
  formState: EmployersFormState | null;
  /** Non-null while a profile is drilled in. Independent of the add/edit form's
   *  state: a profile is always live, a form is a separate screen. */
  detailState: EmployersDetailState | null;
  onSelectStatus: (value: EmployerStatusFilter) => void;
  onToggleSearch: () => void;
  leadingActions: MobileHeaderAction[];
  trailingActions: MobileHeaderAction[];
  pushNotificationsAction: () => void;
  pushBackAction: (targetView: string, label: string, onClickOverride?: () => void) => void;
  /** Returns to the Companies list in Contacts, where employers are opened from. */
  navigateToContacts: () => void;
}

const dispatch = (name: string) => window.dispatchEvent(new CustomEvent(name));

/** Appends Employers pills to the caller's leading/trailing action arrays.
 *  Called only when `currentView === 'employers'`. */
export function buildEmployersHeaderActions(ctx: EmployersHeaderCtx): void {
  const {
    listVisible,
    selectedName,
    statusFilter,
    formState,
    detailState,
    onSelectStatus,
    onToggleSearch,
    leadingActions,
    trailingActions,
    pushNotificationsAction,
    pushBackAction,
    navigateToContacts,
  } = ctx;

  if (formState) {
    // Routed through pushBackAction rather than a hand-rolled id so the chevron
    // is stamped `back-employers`: that prefix is what suppresses the mobile
    // 34pt large title. The old `employer-form-close` id matched neither the
    // prefix nor LARGE_TITLE_BACK_IDS, so the form rendered under a large
    // "Employers" title with 52px of dead clearance above it.
    pushBackAction('employers', 'Back to companies', () => dispatch(EMPLOYER_FORM_CANCEL_EVENT));
    if (formState.hasChanges && !formState.isSubmitting) {
      trailingActions.push({
        // Cancel is a text pill, never an X. An X reads as Close (dismiss the
        // view) rather than discard-my-edits, and a symbol and a word must not
        // share one glass background. Pairs with the Save text pill beside it.
        id: 'employer-form-cancel',
        label: 'Cancel',
        ariaLabel: 'Discard changes',
        onClick: () => dispatch(EMPLOYER_FORM_CANCEL_EVENT),
        group: 'page',
      });
    }
    trailingActions.push({
      id: 'employer-form-save',
      // Icon ONLY while submitting — the spinner replaces the word, matching
      // `workspace-save`. Otherwise this stays a pure text pill.
      icon: formState.isSubmitting ? Loader2 : undefined,
      label: formState.isSubmitting ? 'Saving' : 'Save',
      ariaLabel: formState.isSubmitting
        ? 'Saving employer'
        : formState.hasChanges
          ? (formState.isEdit ? 'Update employer' : 'Save employer')
          : 'No changes to save',
      disabled: formState.isSubmitting || !formState.hasChanges,
      onClick: () => dispatch(EMPLOYER_FORM_SAVE_EVENT),
      group: 'page',
    });
  } else if (selectedName) {
    // Drill-in: the profile replaced the directory, so the chevron is the only
    // way back. It has to exist on every viewport — the profile draws no in-page
    // back control wherever this header renders.
    // Employers have no directory of their own any more: Back returns to the Companies list in Contacts.
    pushBackAction('contacts', 'Back to companies', () => {
      dispatch(EMPLOYERS_CLEAR_SELECTION_EVENT);
      navigateToContacts();
    });

    if (detailState) {
      // Dirtiness alone summons Cancel and Save — there is no edit mode to enter.
      // While dirty they own the row and Email/Call stand down: fitCluster cuts at
      // the first pill that does not fit and collapses everything after it, so
      // leaving four pills here pushed Save into the overflow sheet at every
      // current iPhone width. Clean, both are offered when there is something to
      // reach; icon-only and same 'page' group, so they draw as one capsule.
      const committing = detailState.hasChanges || detailState.isSubmitting;
      if (!committing && detailState.email) {
        trailingActions.push({
          id: 'employer-detail-email',
          icon: Mail,
          ariaLabel: `Email ${selectedName}`,
          onClick: () => { window.location.href = `mailto:${detailState.email}`; },
          group: 'page',
        });
      }
      if (!committing && detailState.phone) {
        trailingActions.push({
          id: 'employer-detail-call',
          icon: Phone,
          ariaLabel: `Call ${selectedName}`,
          onClick: () => { window.location.href = `tel:${detailState.phone}`; },
          group: 'page',
        });
      }
      // Same contract as the add/edit form above: text pills, and neither present
      // on an untouched profile so the header stays quiet until the user actually
      // changes something.
      if (detailState.hasChanges && !detailState.isSubmitting) {
        trailingActions.push({
          id: 'employer-detail-cancel',
          label: 'Cancel',
          ariaLabel: 'Discard changes',
          onClick: () => dispatch(EMPLOYER_DETAIL_CANCEL_EVENT),
          group: 'page',
        });
      }
      if (committing) {
        trailingActions.push({
          id: 'employer-detail-save',
          icon: detailState.isSubmitting ? Loader2 : undefined,
          label: detailState.isSubmitting ? 'Saving' : 'Save',
          ariaLabel: detailState.isSubmitting ? 'Saving employer' : 'Save changes',
          disabled: detailState.isSubmitting,
          onClick: () => dispatch(EMPLOYER_DETAIL_SAVE_EVENT),
          group: 'page',
        });
      }
    }
  } else if (listVisible) {
    // Search is structurally stuck on the leading edge: only renderLeadingAction
    // draws the morph, and on desktop DESKTOP_GLOBAL_SEARCH_IDS relocates it to
    // the persistent far-right field anyway.
    leadingActions.push({
      id: 'employers-search',
      icon: Search,
      ariaLabel: 'Search employers',
      onClick: onToggleSearch,
    });
    const active = EMPLOYER_STATUS_FILTER_OPTIONS.find(o => o.value === statusFilter);
    trailingActions.push({
      // Trailing, group 'view': the filter configures how the directory is
      // shown. Apple's leading edge is a closed enumeration (back/dismiss,
      // sidebar toggle, title, document menu) and a filter is not on it.
      id: 'employers-filter',
      icon: SlidersHorizontal,
      label: active?.label ?? 'All Employers',
      ariaLabel: 'Filter employers by status',
      group: 'view',
      // Load-bearing, not decoration: these are the graceful-degrade path for
      // when the id-intercept does not fire. A pill with neither onClick nor
      // subActions renders completely dead AND becomes non-collapsible, burning
      // 44pt of the mobile row budget on nothing.
      subActions: EMPLOYER_STATUS_FILTER_OPTIONS.map(opt => ({
        id: `ef-${opt.value}`,
        label: opt.label,
        active: (statusFilter ?? EMPLOYER_STATUS_FILTER_ALL) === opt.value,
        onClick: () => onSelectStatus(opt.value),
      })),
    });
    trailingActions.push({
      id: 'employers-add',
      icon: Plus,
      ariaLabel: 'Add employer',
      onClick: () => dispatch(EMPLOYERS_CREATE_EVENT),
      group: 'page',
    });
  }
  pushNotificationsAction();
}
